import { Brand, CreateBrandRequest, StreamEvent } from '@tinct/core-api-models';
import { serializeFieldsQuery } from '../utils/serialize-fields-query';
import { ApiClient } from '../api-client/api-client';
import {
  ApiCollectionResponse,
  FieldQuery,
  GetCollectionQueryParams,
  GetQueryByIdParams,
} from '../types';
import {ApiError} from "../api-client/api-error";

export class BrandsEndpoint {
  constructor(private client: ApiClient) {}

  async getBrand(params: GetQueryByIdParams<Brand>): Promise<Brand> {
    return this.client.getById<Brand>(
      `/api/v1/brands/${params.id}`,
      params.fields,
    );
  }

  async getBrands(
    params: GetCollectionQueryParams<Brand>,
  ): Promise<ApiCollectionResponse<Brand>> {
    const { fields, offset, limit, filters } = params;
    return this.client.getCollection<Brand>('/api/v1/brands', fields, {
      offset,
      limit,
      ...filters,
    });
  }

  /**
   * Creates a new version of the organization's brand (versions are
   * immutable). `data.baseVersion` is the version the caller based its values
   * on: the server answers 409 (error code 5601) when it is not the latest.
   */
  async createBrand(data: CreateBrandRequest, fields?: FieldQuery<Brand>): Promise<Brand> {
    return this.client.post<Brand>('/api/v1/brands', data, {
      params: { fields: fields ? serializeFieldsQuery<Brand>(fields) : undefined },
    });
  }

  /**
   * Starts a brand extraction of the organization from its stored website domain. The server runs
   * it on its own: `onOpen` says it started (the job exists from then on, as the organization's
   * `brandExtractionJob`), and closing the stream never cancels it. Refused (`onError`, 400 with
   * its message) while one runs or when the organization has no domain.
   */
  async initBrandAnalyserAgent(
    organizationId: string,
    onEvent?: (event: StreamEvent) => void,
    onError?: (error: ApiError) => void,
    onComplete?: () => void,
    onOpen?: () => void,
  ): Promise<void> {
    return this.client.postStream(
      `/api/v1/brands/extractor-agent`,
      { organization: { id: organizationId } },
      onEvent,
      onError,
      onComplete,
      onOpen,
    );
  }
}
